<template>
    <div class="product-overview">
        <div v-if="exportingProducts" class="export-status">
            <i class="fa fa-spinner fa-spin" /> {{ trans('products.export.generating') }}
        </div>
        <actions-menu>
            <actions-menu-item
                :v4="true"
                :action="() => exportProductsAsync()"
                icon="table"
                label="Exporteer producten"
            />
            <actions-menu-item
                :v4="true"
                :action="() => openYearEndpoint('/product/exportHistory/')"
                icon="table"
                label="Exporteer geschiedenis"
            />
            <actions-menu-item
                :v4="true"
                :action="
                    () => openYearEndpoint('/product/exportProductionTimes/')
                "
                icon="table"
                label="Exporteer productietijden"
            />
            <slot name="actions" />
        </actions-menu>

        <pre
            class="debug"
            v-if="isDebug"
            >{{ { filters } }}</pre
        >

        <div class="productoverviewline">
            <div class="productoverviewcolumn sort-label">Sortering</div>
            <div class="productoverviewwidecolumn">
                <ui5-select
                    @change="
                        filters.sortField = $event.detail.selectedOption.value
                    "
                >
                    <ui5-option
                        v-for="option in sortFieldOptions"
                        :key="'sortFieldOption.' + option.value"
                        :selected="filters.sortField == option.value"
                        :value="option.value"
                    >
                        {{ option.label }}
                    </ui5-option>
                </ui5-select>
            </div>

            <div class="productoverviewwidecolumn">
                <ui5-select
                    @change="
                        filters.sortOrder = $event.detail.selectedOption.value
                    "
                >
                    <ui5-option
                        v-for="option in sortOrderOptions"
                        :key="'sortOrderOption.' + option.value"
                        :selected="filters.sortOrder == option.value"
                        :value="option.value"
                    >
                        {{ option.label }}
                    </ui5-option>
                </ui5-select>
            </div>

            <div class="productoverviewsmallcolumn">
                <ui5-select
                    @change="
                        filters.sortEmpty = $event.detail.selectedOption.value
                    "
                >
                    <ui5-option
                        v-for="option in emptinessOptions"
                        :key="'emptinessOption.' + option.value"
                        :selected="filters.sortEmpty == option.value"
                        :value="option.value"
                    >
                        {{ option.label }}
                    </ui5-option>
                </ui5-select>
            </div>

            <div class="productoverviewwidecolumn">
                <ui5-button
                    class="productoverviewbtn populate-with-reset"
                    @click="populateWithEmptyList"
                >
                    SORTEER
                </ui5-button>
            </div>

            <div class="productoverviewwidecolumn">
                <ui5-select
                    @change="
                        filters.specialFilter =
                            $event.detail.selectedOption.value
                    "
                >
                    >
                    <ui5-option
                        v-for="option in specialFilterOptions"
                        :key="'specialFilterOption.' + option.value"
                        :selected="filters.specialFilter == option.value"
                        :value="option.value"
                    >
                        {{ option.label }}
                    </ui5-option>
                </ui5-select>
            </div>

            <div class="productoverviewwidecolumn year-column">
                <ui5-select
                    @change="currentYear = $event.detail.selectedOption.value"
                >
                    <ui5-option
                        v-for="year in years"
                        :value="year"
                        :selected="year == currentYear"
                        :key="'year.' + year"
                    >
                        {{ year }}
                    </ui5-option>
                </ui5-select>
            </div>
        </div>
        <div class="productoverviewline">
            <div class="iconcolumn">
                <ui5-checkbox
                    :checked="filters.finished"
                    @change="filters.finished = $event.target.checked"
                />
            </div>
            <div class="iconcolumn"></div>
            <div class="productoverviewcolumnmedium">
                <ui5-input
                    type="Text"
                    :value="filters.productnr"
                    @input="filters.productnr = $event.target.value"
                    @keyup.enter="populateWithEmptyList"
                    @keyup.esc="filters.productnr = storedFilters.productnr"
                    placeholder="Productnr"
                />
            </div>
            <div class="productoverviewcolumnmedium">
                <ui5-input
                    type="Text"
                    :value="filters.ordernr"
                    @input="filters.ordernr = $event.target.value"
                    @keyup.enter="populateWithEmptyList"
                    @keyup.esc="filters.ordernr = storedFilters.ordernr"
                    placeholder="Ordernr"
                />
            </div>
            <div class="productoverviewcolumnlarge">
                <ui5-input
                    type="Text"
                    :value="filters.customer"
                    @input="filters.customer = $event.target.value"
                    @keyup.enter="populateWithEmptyList"
                    @keyup.esc="filters.customer = storedFilters.customer"
                    placeholder="Klant"
                />
            </div>
            <div class="productoverviewcolumnlarge">
                <ui5-input
                    type="Text"
                    :value="filters.reference"
                    @input="filters.reference = $event.target.value"
                    @keyup.enter="populateWithEmptyList"
                    @keyup.esc="filters.reference = storedFilters.reference"
                    placeholder="Referentie"
                />
            </div>
            <div class="productoverviewcolumnmedium auto-complete-column">
                <auto-complete-input
                    v-if="productTypeOptions"
                    v-model="filters.producttype"
                    :initial-search="getProductTypeName(filters.producttype)"
                    :options="productTypeOptions"
                    placeholder="Producttype"
                />
            </div>
            <div class="productoverviewcolumnsmall">
                <ui5-input
                    type="Text"
                    :value="filters.ralnr"
                    @input="filters.ralnr = $event.target.value"
                    @keyup.enter="populateWithEmptyList"
                    @keyup.esc="filters.ralnr = storedFilters.ralnr"
                    placeholder="Ralnr"
                />
            </div>
            <div class="productoverviewcolumnsmall">
                <ui5-input
                    type="Text"
                    :value="filters.raltype"
                    @input="filters.raltype = $event.target.value"
                    @keyup.enter="populateWithEmptyList"
                    @keyup.esc="filters.raltype = storedFilters.raltype"
                    placeholder="Raltype"
                />
            </div>
            <div class="productoverviewcolumnmedium">
                <ui5-input
                    type="Text"
                    :value="filters.poedernr"
                    @input="filters.poedernr = $event.target.value"
                    @keyup.enter="populateWithEmptyList"
                    @keyup.esc="filters.poedernr = storedFilters.poedernr"
                    placeholder="Poedernummer"
                />
            </div>
            <div class="productoverviewcolumnsmall">
                <ui5-input
                    type="Text"
                    :value="filters.status"
                    @input="filters.status = $event.target.value"
                    @keyup.enter="populateWithEmptyList"
                    @keyup.esc="filters.status = storedFilters.status"
                    placeholder="Status"
                />
            </div>
            <div class="productoverviewcolumnmedium auto-complete-column">
                <div>
                    <auto-complete-input
                        v-if="supplierOptions"
                        v-model="filters.supplierId"
                        :initial-search="getSupplierName(filters)"
                        :options="supplierOptions"
                        placeholder="Leverancier"
                    />
                </div>
            </div>
            <div class="productoverviewcolumnmedium">
                <ui5-date-picker
                    class="simplified"
                    @click="$event.target.openPicker()"
                    :value="filters.deliveryDateIN"
                    @change="filters.deliveryDateIN = $event.detail.value"
                    @keyup.enter="populateWithEmptyList"
                    @keyup.esc="
                        filters.deliveryDateIN = storedFilters.deliveryDateIN
                    "
                    placeholder="Lev IN"
                    format-pattern="dd-MM-yyyy"
                />
            </div>
            <div class="productoverviewcolumnmedium">
                <ui5-date-picker
                    class="simplified"
                    @click="$event.target.openPicker()"
                    :value="filters.deliveryDate"
                    @change="filters.deliveryDate = $event.detail.value"
                    @keyup.enter="populateWithEmptyList"
                    @keyup.esc="
                        filters.deliveryDate = storedFilters.deliveryDate
                    "
                    placeholder="Lev UIT"
                    format-pattern="dd-MM-yyyy"
                />
            </div>
            <div class="productoverviewcolumnmedium">
                <ui5-input
                    type="Text"
                    :value="filters.originaldeliveryDate"
                    @input="filters.originaldeliveryDate = $event.target.value"
                    @keyup.enter="populateWithEmptyList"
                    @keyup.esc="
                        filters.originaldeliveryDate =
                            storedFilters.originaldeliveryDate
                    "
                    placeholder="Originele lever #"
                />
            </div>
            <div class="productoverviewcolumnmedium">
                <ui5-input
                    type="Text"
                    :value="filters.deliveryDateId"
                    @input="filters.deliveryDateId = $event.target.value"
                    @keyup.enter="populateWithEmptyList"
                    @keyup.esc="
                        filters.deliveryDateId = storedFilters.deliveryDateId
                    "
                    placeholder="Ritnr"
                />
            </div>
            <div class="productoverviewcolumnmedium">
                <ui5-input
                    type="Text"
                    :value="filters.deliveryMethod"
                    @input="filters.deliveryMethod = $event.target.value"
                    @keyup.enter="populateWithEmptyList"
                    @keyup.esc="
                        filters.deliveryMethod = storedFilters.deliveryMethod
                    "
                    placeholder="Leveringsmethode"
                />
            </div>
            <div class="productoverviewgoto">
                <i
                    class="populate-with-reset fa fa-filter fa-2x"
                    @click="populateWithEmptyList"
                />
            </div>
            <div class="productoverviewgoto">
                <i
                    class="clear-overview fa fa-ban fa-2x"
                    @click="clearOverview"
                />
            </div>
        </div>
        <div id="productoverviewlist">
            <div
                v-for="(product, productIndex) in products"
                class="productoverviewline line-to-count"
                :style="{ 'background-color': getColor(product) }"
                :key="'product' + product.productId"
                :id="'product' + product.productId"
            >
                <div class="iconcolumn">
                    <i
                        class="fa fa-info-circle back"
                        @click="goToProductPage(product.productId)"
                    />
                </div>

                <div class="iconcolumn">
                    <i
                        v-if="product.deliveryDateId"
                        @click="prepareDetailDateForm(product.deliveryDateId)"
                        class="fa fa-truck back"
                    />
                </div>

                <div class="productoverviewcolumnmedium">
                    <ui5-input
                        type="Text"
                        class="productoverviewinput1"
                        :value="product.productNr"
                        readonly
                    />
                </div>
                <div class="productoverviewcolumnmedium">
                    <ui5-input
                        type="Text"
                        class="productoverviewinput2"
                        :value="product.orderId"
                        readonly
                    />
                </div>
                <div class="productoverviewcolumnlarge">
                    <ui5-input
                        type="Text"
                        class="productoverviewinput4"
                        :value="product.customerName"
                        readonly
                    />
                </div>
                <div class="productoverviewcolumnlarge">
                    <ui5-input
                        type="Text"
                        class="productoverviewinput5"
                        :value="product.reference"
                        readonly
                    />
                </div>
                <div class="productoverviewcolumnmedium">
                    <ui5-input
                        type="Text"
                        class="productoverviewinput6"
                        :value="product.producttypeName"
                        readonly
                    />
                </div>
                <div class="productoverviewcolumnsmall">
                    <ui5-input
                        type="Text"
                        class="productoverviewinput7"
                        :value="product.color.ralnr"
                        readonly
                    />
                </div>
                <div class="productoverviewcolumnsmall">
                    <ui5-input
                        type="Text"
                        class="productoverviewinput8"
                        :value="product.color.raltype"
                        readonly
                    />
                </div>
                <div class="productoverviewcolumnmedium">
                    <ui5-input
                        type="Text"
                        class="productoverviewinput9"
                        :value="product.color.poedernr"
                        readonly
                    />
                </div>
                <div class="productoverviewcolumnsmall">
                    <ui5-input
                        type="Text"
                        class="productoverviewinput10"
                        :value="product.productstatus"
                        readonly
                    />
                </div>
                <div class="productoverviewcolumnmedium">
                    <ui5-input
                        type="Text"
                        v-if="supplierOptions"
                        class="productoverviewinput12"
                        :value="getSupplierName(product)"
                        readonly
                    />
                </div>
                <div class="productoverviewcolumnmedium">
                    <ui5-input
                        type="Text"
                        class="productoverviewinput12"
                        :value="product.deliveryDateIN"
                        readonly
                    />
                </div>
                <div class="productoverviewcolumnmedium">
                    <ui5-input
                        type="Text"
                        class="productoverviewinput12"
                        :value="product.deliveryDateDay"
                        readonly
                    />
                </div>
                <div class="productoverviewcolumnmedium">
                    <ui5-input
                        type="Text"
                        class="productoverviewinput13"
                        :value="product.originalDeliveryDate"
                        readonly
                    />
                </div>
                <div class="productoverviewcolumnmedium">
                    <ui5-input
                        type="Text"
                        class="productoverviewinput14"
                        :value="
                            product.deliveryDateKey
                                ? product.deliveryDateKey
                                : ''
                        "
                        readonly
                    />
                </div>
                <div class="productoverviewcolumnmedium">
                    <ui5-input
                        type="Text"
                        class="productoverviewinput15"
                        :value="
                            product.deliveryMethod.name
                                ? product.deliveryMethod.name
                                : ''
                        "
                        readonly
                    />
                </div>
            </div>
        </div>
        <div class="spinner-container">
            <spinner v-if="loading" />
        </div>
        <template v-if="lastAmount < 50">
            <div class="productoverviewline">
                Geen producten meer gevonden met de opgegeven voorwaarden.
            </div>
        </template>
        <div class="productoverviewline">
            Totaal aantal geladen producten:
            <span id="productcount">{{ products.length }}</span>
        </div>
    </div>
</template>
<script>
import ActionsMenu from '../components/ActionsMenu';
import ActionsMenuItem from '../components/ActionsMenuItem';
import AutoCompleteInput from '../components/AutoCompleteInput';
import Spinner from '../components/loader/Spinner';
import api from '../api/api';
import exportApi from '../api/export';
import orderApi from '../api/order';
import supplierApi from '../api/supplier';
import axios from 'axios';
import { stripNonNumeric } from '../helpers/StringHelpers';
import IsDebugMixin from '../mixins/is-debug';
import LocalizationMixin from '../mixins/localization';
import NavigationFunctionsMixin from '../mixins/navigation-functions.js';
import { isFunction } from '../helpers/GeneralHelper';
import { colorArray } from '../shared/colors';
import { showErrorMessage } from '../dialogs';
import _ from 'lodash';
import {
    clearedFilters,
    emptinessOptions,
    sortFieldOptions,
    sortOrderOptions,
    specialFilterOptions,
    fixedSupplierOptions,
    generateSupplierOptions,
} from '../shared/products';
import { getLabel } from '../shared/collections';

export default {
    mixins: [IsDebugMixin, LocalizationMixin, NavigationFunctionsMixin],
    components: {
        AutoCompleteInput,
        ActionsMenu,
        ActionsMenuItem,
        Spinner,
    },
    data() {
        return {
            lastAmount: 50,
            loading: false,
            exportingProducts: false,
            currentYear: null,
            productTypes: null,
            suppliers: null,
            products: [],
            filters: {
                ...clearedFilters,
            },
        };
    },
    props: {
        pageId: {
            required: true,
        },
        endpoint: {
            required: true,
        },
        extraPayload: {
            required: false,
        },
    },
    computed: {
        sortFieldOptions: () => sortFieldOptions,
        sortOrderOptions: () => sortOrderOptions,
        emptinessOptions: () => emptinessOptions,
        specialFilterOptions: () => specialFilterOptions,
        supplierOptions() {
            return generateSupplierOptions(
                this.suppliers,
                fixedSupplierOptions
            );
        },
        productTypeOptions() {
            const options = [{ value: '0', label: 'Alle producttypes' }];
            if (this.productTypes) {
                this.productTypes.forEach((productType) => {
                    options.push({
                        value: productType.producttypeId,
                        label: productType.label,
                    });
                });
            }
            return options;
        },
        years() {
            return _.range(2010, new Date().getFullYear() + 1);
        },
        storedFilters: {
            get() {
                const getter =
                    this.$store.getters['productOverviews/getFilters'];
                return { ...getter(this.pageId) };
            },
            set(value) {
                this.$store.commit(
                    'productOverviews/setFilters',
                    { pageKey: this.pageId, filters: { ...value } },
                    { root: true }
                );
            },
        },
    },
    mounted() {
        this.currentYear = new Date().getFullYear();
        this.products = [];

        this.filters = { ...this.filters, ...this.storedFilters };

        orderApi.productTypesForOrder().then((response) => {
            this.productTypes = response.data.data;
            this.load();
        });

        supplierApi.fetchAllSuppliers().then((response) => {
            this.suppliers = response.data.data;
        });

        this.$bus.$on('bottom-scroll', this.load);
    },
    beforeRouteLeave(to, from, next) {
        this.$bus.$off('bottom-scroll', this.load);
        next();
    },
    methods: {
        getProductTypeName(id) {
            return getLabel(id, this.productTypeOptions);
        },
        getSupplierName(product) {
            const supplierId =
                product && product.supplierId ? product.supplierId : null;
            return getLabel(supplierId, this.supplierOptions);
        },
        prepareDetailDateForm(dateId) {
            this.$router.push('/deliverydatedetails/' + dateId);
        },
        load() {
            if (this.loading) {
                return;
            }

            this.storedFilters = this.filters;

            let payload = {};

            payload.finished = this.filters.finished ? 1 : 0;
            payload.productnr = stripNonNumeric(this.filters.productnr);
            payload.ordernr = this.filters.ordernr;
            payload.customer = this.filters.customer;
            payload.reference = this.filters.reference;
            payload.producttype = this.filters.producttype;
            payload.ralnr = this.filters.ralnr;
            payload.raltype = this.filters.raltype;
            payload.poedernr = this.filters.poedernr;
            payload.status = this.filters.status;
            payload.productionDate = this.filters.productionDate;
            payload.deliveryDate = this.filters.deliveryDate;
            payload.deliveryDateIN = this.filters.deliveryDateIN;
            payload.originaldeliveryDate = this.filters.originaldeliveryDate;
            payload.deliveryDateId = this.filters.deliveryDateId;
            payload.deliveryMethod = this.filters.deliveryMethod;
            payload.sortField = this.filters.sortField;
            payload.sortOrder = this.filters.sortOrder;
            payload.sortEmpty = this.filters.sortEmpty;
            payload.specialFilter = this.filters.specialFilter;
            payload.supplierId = this.filters.supplierId;

            if (this.extraPayload) {
                if (isFunction(this.extraPayload)) {
                    payload = { ...payload, ...this.extraPayload() };
                } else {
                    payload = { ...payload, ...this.extraPayload };
                }
            }

            payload.offset = this.products.length;
            payload.limit = 50;

            this.loading = true;
            axios
                .post(this.endpoint, payload)
                .then((response) => {
                    const products = response.data.data;
                    this.products = [...this.products, ...products];
                    this.lastAmount = products.length;
                })
                .catch(() => {
                    showErrorMessage('Fout bij laden');
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        getColor(product) {
            const colorIndex = Math.min(Math.max(product.daysPastDue, 0), 9);
            return colorArray[colorIndex];
        },
        async exportProductsAsync() {
            const MAX_POLLING_ATTEMPTS = 150;
            const POLLING_INTERVAL_MS = 2000;

            this.exportingProducts = true;
            try {
                const { data } = await exportApi.requestProductExport(this.currentYear);
                let attempts = 0;
                let exportCompleted = false;
                while (attempts < MAX_POLLING_ATTEMPTS) {
                    await new Promise((r) => setTimeout(r, POLLING_INTERVAL_MS));
                    const { data: exportStatus } = await exportApi.checkProductExport(data.id);
                    if (exportStatus.status === 'completed') {
                        const { data: blob } = await exportApi.downloadProductExport(data.id);
                        const url = URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `products-export-${this.currentYear}.csv`;
                        a.click();
                        URL.revokeObjectURL(url);
                        exportCompleted = true;
                        break;
                    }
                    if (exportStatus.status === 'failed') {
                        showErrorMessage(this.trans('products.export.error'));
                        break;
                    }
                    attempts++;
                }
                if (attempts >= MAX_POLLING_ATTEMPTS && !exportCompleted) {
                    showErrorMessage(this.trans('products.export.timeout'));
                }
            } catch (e) {
                console.error('Product export failed', e);
                showErrorMessage(this.trans('products.export.error'));
            } finally {
                this.exportingProducts = false;
            }
        },
        openYearEndpoint(endpoint) {
            const payload = {
                year: this.currentYear,
                employeeId: this.$store.getters['currentUser/getUserId'],
            };
            window.open(api.createGetUrl(endpoint, payload));
        },
        populateWithEmptyList() {
            this.products = [];
            this.load();
        },
        clearOverview() {
            this.filters = { ...clearedFilters };
            this.populateWithEmptyList();
        },
    },
};
</script>
